import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { requiredValue } from '@nestech/utils';
import { AvailableLanguage, EnvironmentService } from '../environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class POEditorTranslationHelper implements OnDestroy {
  #observer: MutationObserver | null = null;
  readonly #pattern = /\bcas\.\S+/g;
  #processedNodes = new WeakSet<Node>();
  readonly #poEditorProjectId = '517935';
  readonly #poEditorLanguageId = inject(EnvironmentService).environment.poEditor.languageId;
  readonly #translateService = inject(TranslateService);

  constructor() {
    const zone = inject(NgZone);
    const isEnabled = localStorage.getItem('liveI18NMode') === '"noFetch"';
    if (!isEnabled) return;
    zone.runOutsideAngular(() => {
      this.#injectStyles();
      this.#createNotificationBox();
      this.#setupShiftKeyListeners();
      this.#setupObserver();
    });
  }

  #injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .cas-clickable {
        cursor: context-menu;
      }
      body.shift-pressed .cas-clickable {
        text-decoration: underline;
        text-decoration-style: dotted;
        text-decoration-color: currentColor;
        text-underline-offset: 2px;
      }
      body.shift-pressed .cas-clickable:hover {
        text-decoration-style: solid;
      }
      body.shift-pressed #po-editor-notification {
        opacity: 0;
      }
    `;
    document.head.appendChild(style);
  }

  #createNotificationBox(): void {
    const notification = document.createElement('div');
    notification.id = 'po-editor-notification';
    notification.className = 'position-fixed top-0 start-50 translate-middle-x mt-1 alert alert-info';
    notification.style.zIndex = '10000';
    notification.style.transition = 'opacity 0.3s ease';
    notification.innerHTML =
      '<small class="fs-6 d-flex align-items-center">Hold <kbd class="mx-1">Shift</kbd> nede for at redigere teksten i POEditor</small>';
    document.body.appendChild(notification);
  }

  #setupShiftKeyListeners(): void {
    document.addEventListener('keydown', e => {
      if (e.key === 'Shift' && !document.body.classList.contains('shift-pressed')) {
        document.body.classList.add('shift-pressed');
      }
    });

    document.addEventListener('keyup', e => {
      if (e.key === 'Shift') {
        document.body.classList.remove('shift-pressed');
      }
    });

    // Handle when window loses focus
    window.addEventListener('blur', () => {
      document.body.classList.remove('shift-pressed');
    });
  }

  #setupObserver(): void {
    this.#processExistingContent(document.body);

    this.#observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              this.#processTextNode(node as Text);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              this.#processElement(node as Element);
            }
          });
        } else if (mutation.type === 'characterData') {
          const textNode = mutation.target as Text;
          if (!this.#processedNodes.has(textNode)) {
            this.#processTextNode(textNode);
          }
        }
      }
    });

    this.#observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true,
    });
  }

  #processExistingContent(element: Element): void {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: (node: Text) => {
        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      this.#processTextNode(node);
    }
  }

  #processElement(element: Element): void {
    if (element.classList?.contains('cas-clickable')) {
      return;
    }
    this.#processExistingContent(element);
  }

  #processTextNode(textNode: Text): void {
    const text = textNode.textContent;
    if (!text || this.#processedNodes.has(textNode)) {
      return;
    }

    const matches = [...text.matchAll(this.#pattern)];
    if (matches.length === 0) {
      return;
    }

    const parent = textNode.parentNode;
    if (!parent || (parent as Element).classList?.contains('cas-clickable')) {
      return;
    }

    this.#processedNodes.add(textNode);

    // A node that is one whole key is wrapped in place, so Angular's later writes to it (a key that
    // changes with state) stay visible. Only a node mixing keys and other text is split into a copy.
    if (matches.length === 1 && matches[0][0] === text.trim()) {
      const span = this.#createClickableSpan();
      parent.insertBefore(span, textNode);
      span.appendChild(textNode);
      return;
    }

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    matches.forEach(match => {
      const matchText = match[0];
      const matchIndex = requiredValue(match.index, 'Match index');

      if (matchIndex > lastIndex) {
        const beforeText = text.substring(lastIndex, matchIndex);
        fragment.appendChild(document.createTextNode(beforeText));
      }

      const span = this.#createClickableSpan();
      span.textContent = matchText;
      fragment.appendChild(span);

      lastIndex = matchIndex + matchText.length;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    parent.replaceChild(fragment, textNode);
  }

  #createClickableSpan(): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'cas-clickable';
    span.addEventListener('click', event => {
      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        this.#openPOEditor(span.textContent?.trim() ?? '');
      }
    });
    return span;
  }

  #openPOEditor(text: string): void {
    // Extract the last part after the last dot (e.g., cas.xyz.abc -> abc)
    const parts = text.split('.');
    const searchTerm = parts[parts.length - 1];

    const currentLang = this.#translateService.getCurrentLang() as AvailableLanguage;
    const urlFn = (languageId: number) =>
      `https://poeditor.com/projects/po_edit?search=em:+terms:+${searchTerm}&per_page=20&id=${this.#poEditorProjectId}&id_language=${languageId}`;

    if (this.#poEditorLanguageId[currentLang]) {
      window.open(urlFn(this.#poEditorLanguageId[currentLang]), `${currentLang}-poeditor`);
    } else {
      Object.values(this.#poEditorLanguageId).forEach(langId => window.open(urlFn(langId), `${langId}-poeditor`));
    }
  }

  ngOnDestroy(): void {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    this.#processedNodes = new WeakSet();

    // Clean up notification box
    const notification = document.getElementById('po-editor-notification');
    if (notification) {
      notification.remove();
    }

    // Remove shift-pressed class
    document.body.classList.remove('shift-pressed');
  }
}
